// src/utils/authFlow.js
// Pure helpers for registration and sign-in (Roadmap #88, registration confirm-first;
// docs/briefs/2026-10-03-registration-confirm-first.md + addenda 1 and 2). No data access here.
// The password minimum and display-name maximum live in validationConstants.js (addendum 2 item 1).
//
// 1. authErrorMessage — a Supabase Auth error → { field, key }: which field the message belongs to
//    and which auth-namespace translation key says it. Decided by error.code (and, for a weak
//    password, error.reasons), never by Supabase's English wording (addendum 1, item 4). Used by
//    sign-up and sign-in, and (context 'password') by password reset and the profile's
//    change-password (addendum 2 item 1).
//    NOTE for the translation audit: the keys below are passed to t() through a variable, so a
//    grep for t('…') will not find them (TRANSLATION.md § Common Pitfalls #1) — among them
//    error_password_same, error_password_reauth, error_password_change_failed and
//    confirm_email_first.
// 2. The invitation carrier (Invitations Build 2 §1, built here per addendum 1 item 3): the
//    personal invitation or shared join-link token rides in the sign-up confirmation link,
//    origin/#/auth/callback?invite=<token> (or ?join=<token>), so it survives the email round trip
//    on any device. Nothing is stored server-side before the person acts.

// The resend button's wait after each confirmation email (Auth's 60-second minimum per user).
export const RESEND_WAIT_SECONDS = 60;

// Personal invitation and shared join-link tokens share this shape (20261003b, _verify_signed_token).
// The pattern's source is exported so redaction (src/utils/redactTokens.js) recognises exactly the
// same tokens the routes and the carrier accept — one definition.
export const TOKEN_SHAPE_SOURCE = '[0-9a-fA-F]+\\.[0-9a-fA-F]{64}';
const TOKEN_SHAPE = new RegExp(`^${TOKEN_SHAPE_SOURCE}$`);
const TOKEN_MAX_LENGTH = 2000;

export function isInvitationTokenShape(token) {
  return typeof token === 'string' && token.length <= TOKEN_MAX_LENGTH && TOKEN_SHAPE.test(token);
}

function isNetworkError(error) {
  if (!error) return false;
  if (error.name === 'AuthRetryableFetchError') return true;
  if (error.status === 0) return true;
  return error instanceof TypeError; // fetch() itself rejected (offline, DNS)
}

/**
 * @param {object} error — what supabase.auth.* returned (or threw)
 * @param {'register'|'login'|'password'} context
 * @returns {{ field: 'email'|'password'|'general', key: string, known: boolean }}
 *   known=false: an unmapped error — the caller shows the general message and logs the raw one.
 */
export function authErrorMessage(error, context = 'register') {
  if (isNetworkError(error)) return { field: 'general', key: 'error_network', known: true };
  const code = error?.code;
  switch (code) {
    case 'weak_password': {
      const reasons = Array.isArray(error?.reasons) ? error.reasons : [];
      if (reasons.includes('pwned')) return { field: 'password', key: 'error_password_pwned', known: true };
      if (reasons.includes('characters')) return { field: 'password', key: 'error_password_characters', known: true };
      // Supabase's own length refusal: production's minimum is not known here, so no number.
      return { field: 'password', key: 'error_password_length', known: true };
    }
    case 'user_already_exists':
    case 'email_exists':
      return { field: 'email', key: 'account_exists', known: true };
    case 'email_address_invalid':
    case 'email_address_not_authorized':
      return { field: 'email', key: 'email_invalid', known: true };
    case 'email_not_confirmed':
      return { field: 'general', key: 'confirm_email_first', known: true };
    case 'invalid_credentials':
      return { field: 'general', key: 'invalid_credentials', known: true };
    case 'over_email_send_rate_limit':
      return { field: 'general', key: 'error_email_rate_limited', known: true };
    case 'over_request_rate_limit':
      return { field: 'general', key: 'error_rate_limited', known: true };
    case 'signup_disabled':
    case 'email_provider_disabled':
      return { field: 'general', key: 'error_signup_disabled', known: true };
    case 'request_timeout':
      return { field: 'general', key: 'error_network', known: true };
    case 'same_password':
      return { field: 'password', key: 'error_password_same', known: true };
    case 'reauthentication_needed':
      return { field: 'general', key: 'error_password_reauth', known: true };
    default: {
      const fallback = { login: 'login_failed', password: 'error_password_change_failed' }[context] || 'register_failed';
      return { field: 'general', key: fallback, known: false };
    }
  }
}

/** The confirmation link's return address, carrying an invitation if there is one. */
export function buildSignupRedirect(origin, inviteContext) {
  const base = `${origin}/#/auth/callback`;
  if (!inviteContext || !isInvitationTokenShape(inviteContext.token)) return base;
  const param = inviteContext.type === 'join' ? 'join' : 'invite';
  return `${base}?${param}=${encodeURIComponent(inviteContext.token)}`;
}

/**
 * The invitation carried back by a confirmation link, read from the address before it is cleared.
 * Supabase appends its own "#access_token=…" (or "#error=…") after ours, so only the part before
 * that second "#" is ours. Anything that is not exactly one well-formed token is ignored.
 * @returns {{ type: 'invite'|'join', token: string } | null}
 */
export function parseCallbackCarrier(hash) {
  if (typeof hash !== 'string' || !hash.startsWith('#/auth/callback?')) return null;
  const ours = hash.slice('#/auth/callback?'.length).split('#')[0];
  const params = new URLSearchParams(ours);
  for (const type of ['invite', 'join']) {
    const values = params.getAll(type);
    if (values.length === 1 && isInvitationTokenShape(values[0])) return { type, token: values[0] };
  }
  return null;
}

/**
 * A confirmation link that failed (expired, already used): Supabase lands with "error=…" and
 * "error_code=…" in place of the tokens, either as its own "#…" fragment or after our "?".
 * Returns its error_code (or error), or null.
 */
export function parseCallbackError(hash) {
  if (typeof hash !== 'string') return null;
  for (const part of hash.split('#')) {
    const query = part.includes('?') ? part.slice(part.indexOf('?') + 1) : part;
    const params = new URLSearchParams(query);
    if (params.has('error_code') || params.has('error')) {
      return params.get('error_code') || params.get('error');
    }
  }
  return null;
}
